//React
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import DOMPurify from 'dompurify';

//Components
import { useClient } from '../ClientContext';
import RichTextEditor from './RichTextEditor';

//Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

const styles = {
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    mb: '32px',
    p: '24px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: '16px',
  },
  title: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '24px',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#344054',
  },
  noteTitle: {
    fontWeight: 600,
    fontSize: '18px',
  },
  noteVersion: {
    fontSize: '14px',
    color: '#475467',
    fontWeight: 'bold',
    mr: '8px',
  },
  noteDate: {
    fontSize: '12px',
    fontStyle: 'italic',
    color: '#98A2B3',
  },
  addButton: {
    backgroundColor: '#772EDD',
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 'bold',
    boxShadow: 'none',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#5C21BF',
    },
  },
  deleteButton: {
    color: '#F04438',
    '&:hover': {
      backgroundColor: 'rgba(240, 68, 56, 0.1)',
    },
  },
  editButton: {
    color: '#1570EF',
    '&:hover': {
      backgroundColor: 'rgba(21, 112, 239, 0.1)',
    },
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100vw - 370px)',
    maxWidth: '95vw',
    maxHeight: '85vh',
    boxSizing: 'border-box',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  submitButton: {
    backgroundColor: '#772EDD',
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 'bold',
    minWidth: '100px',
    height: '40px',
    '&:hover': {
      backgroundColor: '#5C21BF',
    },
  },
  cancelButton: {
    color: '#475467',
    borderColor: '#475467',
    fontWeight: 'bold',
    minWidth: '100px',
    height: '40px',
    '&:hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#344054',
    },
  },
  accordion: {
    mb: '8px',
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    display: 'flex',
    alignItems: 'center',
  },
};

const ReleaseNotes = ({ accessToken }) => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newNote, setNewNote] = useState({ version: '', title: '', notes: '', date: '' });
  const [editingNote, setEditingNote] = useState(null);
  const [applyToAllCompanies, setApplyToAllCompanies] = useState(false);
  const [suggestedVersions, setSuggestedVersions] = useState({ major: '', minor: '', patch: '', current: null });
  const [containerWidth, setContainerWidth] = useState(null);
  const containerRef = useRef(null);
  const { selectedClient, config } = useClient();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(Math.round(containerRef.current.getBoundingClientRect().width));
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [modalOpen]);

  // Get the dashboardKey for the selected client from config
  const dashboardKey = config?.companyToDashboardKey?.[selectedClient] || null;
  const showCompanyScopeToggle = dashboardKey !== 'AIMD';

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid date';
    }
  };

  const fetchReleaseNotes = useCallback(async () => {
    if (!selectedClient || !dashboardKey) return;

    setLoading(true);
    try {
      // Build URL with dashboardKey (required) and company (optional filter)
      const params = new URLSearchParams({ dashboardKey });
      if (selectedClient) {
        params.append('company', selectedClient);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/release-notes?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setReleaseNotes(data.releaseNotes || []);
      setError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching release notes:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedClient, dashboardKey, accessToken]);

  useEffect(() => {
    fetchReleaseNotes();
  }, [fetchReleaseNotes]);

  // Fetch the next available versions for the current dashboardKey/company combination
  const fetchSuggestedVersions = useCallback(
    async (forAllCompanies = false) => {
      if (!dashboardKey) return;

      try {
        const params = new URLSearchParams({ dashboardKey });
        // If NOT applying to all companies, filter by the selected company
        if (!forAllCompanies && selectedClient) {
          params.append('company', selectedClient);
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/release-notes/versions?${params}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSuggestedVersions(data.versions || { major: '1.0.0', minor: '1.0.0', patch: '1.0.0', current: null });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching suggested versions:', err);
      }
    },
    [dashboardKey, selectedClient, accessToken],
  );

  // Fetch suggested versions when modal opens or toggle changes (only for new notes)
  useEffect(() => {
    if (modalOpen && !editingNote) {
      fetchSuggestedVersions(applyToAllCompanies);
    }
  }, [modalOpen, editingNote, applyToAllCompanies, fetchSuggestedVersions]);

  // Auto-populate version field with suggested patch version when creating new note
  useEffect(() => {
    if (modalOpen && !editingNote && suggestedVersions.patch) {
      setNewNote((prev) => ({ ...prev, version: suggestedVersions.patch }));
    }
  }, [modalOpen, editingNote, suggestedVersions.patch]);

  const handleUpsertNote = async (e) => {
    e.preventDefault();

    // Validate required fields
    const trimmedVersion = newNote.version?.trim();
    const trimmedTitle = newNote.title?.trim();
    // Strip HTML tags to check if notes has actual content
    const notesText = newNote.notes?.replace(/<[^>]*>/g, '').trim();

    if (!trimmedVersion) {
      setError('Version is required');
      return;
    }
    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }
    if (!notesText) {
      setError('Notes cannot be empty');
      return;
    }
    if (!newNote.date) {
      setError('Release Date is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Send notes as HTML string for rich text support
      // Use new consolidated endpoints: POST /release-notes or PATCH /release-notes/:id
      const url = editingNote
        ? `${process.env.REACT_APP_BACKEND_URL}/release-notes/${editingNote.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/release-notes`;

      const response = await fetch(url, {
        method: editingNote ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          version: newNote.version,
          title: newNote.title,
          notes: newNote.notes, // Send as HTML string
          releaseDate: newNote.date,
          dashboardKey: dashboardKey,
          company: showCompanyScopeToggle && applyToAllCompanies ? null : selectedClient, // null = applies to all companies
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editingNote ? 'update' : 'create'} release note`);
      }

      // Refresh the list
      await fetchReleaseNotes();

      // Close modal and reset form
      setModalOpen(false);
      setNewNote({ version: '', title: '', notes: '', date: '' });
      setEditingNote(null);
      setApplyToAllCompanies(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error ${editingNote ? 'updating' : 'creating'} release note:`, error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this release note?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use new consolidated endpoint: DELETE /release-notes/:id
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/release-notes/${noteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete release note');
      }

      // Refresh the list
      await fetchReleaseNotes();
    } catch (error) {
      console.error('Error deleting release note:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (note, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingNote(note);

    // Handle both legacy array format and new HTML string format
    const notesContent = Array.isArray(note.notes)
      ? `<ul>${note.notes.map((line) => `<li>${line}</li>`).join('')}</ul>`
      : note.notes;

    // Format date for input (YYYY-MM-DD) - support both old 'date' field and new 'releaseDate' field
    const dateValue = note.releaseDate || note.date;
    const formattedDate = dateValue ? new Date(dateValue).toISOString().split('T')[0] : '';

    setNewNote({
      version: note.version,
      title: note.title,
      notes: notesContent,
      date: formattedDate,
    });

    // Set toggle based on whether note has a company (null = applies to all)
    setApplyToAllCompanies(showCompanyScopeToggle && note.company === null);
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNote((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box ref={containerRef} sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>Release Notes</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={styles.addButton}
          onClick={() => setModalOpen(true)}
          disabled={loading || !selectedClient || !dashboardKey || selectedClient === 'All Dashboards'}
        >
          Add Release Note
        </Button>
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}

      {loading && <Typography sx={styles.subtitle}>Loading...</Typography>}

      {!loading && releaseNotes.length === 0 && (
        <Typography sx={styles.subtitle}>No release notes available for {selectedClient}</Typography>
      )}

      {releaseNotes.length > 0 &&
        releaseNotes.map((note) => (
          <Accordion key={note.id} sx={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-expandIconWrapper': {
                  position: 'relative',
                  right: 'auto',
                  marginLeft: '8px',
                  marginRight: 'auto',
                },
                flexDirection: 'row',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ ...styles.noteTitle, display: 'flex', alignItems: 'center' }}>
                    <Typography component="span" sx={styles.noteVersion}>
                      v{note.version}
                    </Typography>
                    <span>{note.title}</span>
                  </Typography>
                  {showCompanyScopeToggle && note.company === null && (
                    <Chip
                      label="All Companies"
                      size="small"
                      sx={{
                        backgroundColor: '#E3F2FD',
                        color: '#1565C0',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: '22px',
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={styles.noteDate} style={{ marginRight: '12px' }}>
                    {formatDate(note.releaseDate || note.date)}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={styles.editButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditNote(note, e);
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteNote(note.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  ...styles.subtitle,
                  '& ul, & ol': { paddingLeft: '20px', margin: '8px 0' },
                  '& li': { marginBottom: '4px' },
                  '& p': { margin: '4px 0' },
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: '4px', margin: '8px 0' },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    Array.isArray(note.notes)
                      ? note.notes.length === 1 && /<[^>]+>/.test(note.notes[0])
                        ? note.notes[0] // New format: single HTML string
                        : `<ul>${note.notes.map((line) => `<li>${line}</li>`).join('')}</ul>` // Legacy format: plain text array
                      : note.notes,
                  ),
                }}
              />
            </AccordionDetails>
          </Accordion>
        ))}

      {/* Create/Edit Release Note Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingNote(null);
          setNewNote({ version: '', title: '', notes: '', date: '' });
          setApplyToAllCompanies(false);
        }}
        aria-labelledby="release-note-modal"
      >
        <Paper
          sx={{
            ...styles.modal,
            ...(containerWidth ? { width: `${containerWidth}px` } : {}),
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {editingNote ? 'Edit Release Note' : 'Create New Release Note'}
          </Typography>
          <form onSubmit={handleUpsertNote} style={styles.form}>
            {/* Apply to all companies toggle */}
            {showCompanyScopeToggle && (
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={applyToAllCompanies}
                      onChange={(e) => setApplyToAllCompanies(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Apply to all companies
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {applyToAllCompanies
                          ? `This release note will be visible to all companies using the ${dashboardKey} dashboard`
                          : `This release note will only be visible to ${selectedClient}`}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* Version field with suggested next versions */}
            <Box>
              <TextField
                label="Version"
                name="version"
                value={newNote.version}
                required
                placeholder="1.0.0"
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input': { cursor: 'default' } }}
                helperText={
                  !editingNote && suggestedVersions.current
                    ? `Current version: ${suggestedVersions.current}`
                    : !editingNote
                      ? 'No existing versions'
                      : undefined
                }
              />
              {!editingNote && (suggestedVersions.major || suggestedVersions.minor || suggestedVersions.patch) && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Available:
                  </Typography>
                  <Chip
                    label={`${suggestedVersions.patch} (patch)`}
                    size="small"
                    onClick={() => setNewNote((prev) => ({ ...prev, version: suggestedVersions.patch }))}
                    color={newNote.version === suggestedVersions.patch ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label={`${suggestedVersions.minor} (minor)`}
                    size="small"
                    onClick={() => setNewNote((prev) => ({ ...prev, version: suggestedVersions.minor }))}
                    color={newNote.version === suggestedVersions.minor ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label={`${suggestedVersions.major} (major)`}
                    size="small"
                    onClick={() => setNewNote((prev) => ({ ...prev, version: suggestedVersions.major }))}
                    color={newNote.version === suggestedVersions.major ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              label="Title"
              name="title"
              value={newNote.title}
              onChange={handleInputChange}
              required
              placeholder="New Features and Improvements"
              fullWidth
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                Notes *
              </Typography>
              <RichTextEditor
                value={newNote.notes}
                onChange={(html) => setNewNote((prev) => ({ ...prev, notes: html }))}
                placeholder="Add release note details... Use the toolbar for formatting."
              />
            </Box>
            <TextField
              label="Release Date"
              name="date"
              type="date"
              value={newNote.date}
              onChange={handleInputChange}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                sx={styles.cancelButton}
                onClick={() => {
                  setModalOpen(false);
                  setEditingNote(null);
                  setNewNote({ version: '', title: '', notes: '', date: '' });
                  setApplyToAllCompanies(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={styles.submitButton} disabled={loading}>
                {loading ? 'Saving...' : editingNote ? 'Update' : 'Save'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Modal>
    </Box>
  );
};

export default ReleaseNotes;
